/**
 * Password Generator Utility
 * Generates cryptographically-random passwords for new user accounts.
 */

const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz'
const DIGITS = '0123456789'
const SYMBOLS = '!@#$%^&*'
const ALL_CHARS = UPPERCASE + LOWERCASE + DIGITS + SYMBOLS

const PASSWORD_LENGTH = 12

/**
 * Returns a random integer in [0, max) using crypto.getRandomValues when
 * available (browser + Node 15+), falling back to Math.random otherwise.
 */
function randomInt(max: number): number {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const arr = new Uint32Array(1)
    crypto.getRandomValues(arr)
    return arr[0] % max
  }
  return Math.floor(Math.random() * max)
}

/**
 * Generates a random 12-character password that always contains at least one
 * uppercase letter, one lowercase letter, one digit and one symbol.
 */
export function generateRandomPassword(): string {
  const required = [
    UPPERCASE[randomInt(UPPERCASE.length)],
    LOWERCASE[randomInt(LOWERCASE.length)],
    DIGITS[randomInt(DIGITS.length)],
    SYMBOLS[randomInt(SYMBOLS.length)],
  ]

  const rest = Array.from({ length: PASSWORD_LENGTH - required.length }, () =>
    ALL_CHARS[randomInt(ALL_CHARS.length)]
  )

  // Fisher-Yates shuffle so required chars aren't always at the front
  const chars = [...required, ...rest]
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1)
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }

  return chars.join('')
}
